import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

export interface JsonViewerProps {
  value: unknown;
  /** Levels initially expanded. Default 2. */
  defaultExpandedDepth?: number;
  className?: string;
}

/**
 * Editorial JSON viewer. Collapsible objects/arrays with token colors:
 *   keys: zinc-300, strings: emerald-300, numbers: amber-300,
 *   booleans: violet-300, null/undefined: zinc-500.
 * Uses Geist Mono via the `.font-mono` utility.
 */
export function JsonViewer({
  value,
  defaultExpandedDepth = 2,
  className,
}: JsonViewerProps) {
  return (
    <pre
      className={cn(
        "font-mono text-xs leading-relaxed text-zinc-300",
        "overflow-x-auto rounded-md border border-zinc-800 bg-zinc-950/60 p-3",
        className,
      )}
    >
      <Node value={value} depth={0} defaultExpandedDepth={defaultExpandedDepth} />
    </pre>
  );
}

function Node({
  value,
  depth,
  defaultExpandedDepth,
  trailingComma = false,
}: {
  value: unknown;
  depth: number;
  defaultExpandedDepth: number;
  trailingComma?: boolean;
}) {
  if (value === null) return <Leaf token={<span className="text-zinc-500">null</span>} trailingComma={trailingComma} />;
  if (value === undefined) return <Leaf token={<span className="text-zinc-500">undefined</span>} trailingComma={trailingComma} />;
  const t = typeof value;
  if (t === "string") {
    return <Leaf token={<span className="text-emerald-300">"{escapeStr(value as string)}"</span>} trailingComma={trailingComma} />;
  }
  if (t === "number") {
    return <Leaf token={<span className="text-amber-300">{String(value)}</span>} trailingComma={trailingComma} />;
  }
  if (t === "boolean") {
    return <Leaf token={<span className="text-violet-300">{String(value)}</span>} trailingComma={trailingComma} />;
  }
  if (Array.isArray(value)) {
    return (
      <Collapsible
        depth={depth}
        defaultExpandedDepth={defaultExpandedDepth}
        open="["
        close="]"
        emptySummary="[]"
        count={value.length}
        trailingComma={trailingComma}
      >
        {value.map((v, i) => (
          <Row key={i} depth={depth + 1}>
            <Node
              value={v}
              depth={depth + 1}
              defaultExpandedDepth={defaultExpandedDepth}
              trailingComma={i < value.length - 1}
            />
          </Row>
        ))}
      </Collapsible>
    );
  }
  if (t === "object") {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    return (
      <Collapsible
        depth={depth}
        defaultExpandedDepth={defaultExpandedDepth}
        open="{"
        close="}"
        emptySummary="{}"
        count={keys.length}
        trailingComma={trailingComma}
      >
        {keys.map((k, i) => (
          <Row key={k} depth={depth + 1}>
            <span className="text-zinc-300">"{k}"</span>
            <span className="text-zinc-500">: </span>
            <Node
              value={obj[k]}
              depth={depth + 1}
              defaultExpandedDepth={defaultExpandedDepth}
              trailingComma={i < keys.length - 1}
            />
          </Row>
        ))}
      </Collapsible>
    );
  }
  return <Leaf token={<span className="text-zinc-500">{String(value)}</span>} trailingComma={trailingComma} />;
}

function Leaf({
  token,
  trailingComma,
}: {
  token: React.ReactNode;
  trailingComma: boolean;
}) {
  return (
    <span>
      {token}
      {trailingComma && <span className="text-zinc-500">,</span>}
    </span>
  );
}

function Row({
  depth,
  children,
}: {
  depth: number;
  children: React.ReactNode;
}) {
  return (
    <div style={{ paddingLeft: depth * 12 }} className="flex">
      <span className="select-none text-zinc-700">{indent(depth)}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}

function Collapsible({
  depth,
  defaultExpandedDepth,
  open,
  close,
  emptySummary,
  count,
  trailingComma,
  children,
}: {
  depth: number;
  defaultExpandedDepth: number;
  open: "[" | "{";
  close: "]" | "}";
  emptySummary: string;
  count: number;
  trailingComma: boolean;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = React.useState(depth < defaultExpandedDepth);
  if (count === 0) {
    return (
      <span>
        <span className="text-zinc-500">{emptySummary}</span>
        {trailingComma && <span className="text-zinc-500">,</span>}
      </span>
    );
  }
  return (
    <span className="inline-block align-top">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="inline-flex items-center gap-0.5 rounded-sm px-0.5 text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <span className="text-zinc-500">{open}</span>
        {!expanded && (
          <span className="ml-1 text-zinc-600">
            {count} {open === "[" ? (count === 1 ? "item" : "items") : count === 1 ? "key" : "keys"}
            <span className="text-zinc-500"> {close}</span>
          </span>
        )}
      </button>
      {expanded && (
        <>
          <div>{children}</div>
          <div style={{ paddingLeft: depth * 12 }}>
            <span className="select-none text-zinc-700">{indent(depth)}</span>
            <span className="text-zinc-500">{close}</span>
            {trailingComma && <span className="text-zinc-500">,</span>}
          </div>
        </>
      )}
    </span>
  );
}

function indent(_: number): string {
  // Visual indentation is handled by paddingLeft on Row; this returns nothing
  // but keeps the spacing column for alignment if needed later.
  return "";
}

function escapeStr(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t");
}
